import ReactECharts from "echarts-for-react";
import { ServiceRevenueMetric } from "@/services/metrics";
import { formatCurrency } from "@/lib/utils";
import { Loader2 } from "lucide-react";

interface ServiceRevenueChartProps {
  data: ServiceRevenueMetric[];
  isLoading: boolean;
}

export function ServiceRevenueChart({ data, isLoading }: ServiceRevenueChartProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[300px]">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-[300px] text-muted-foreground text-sm">
        Sin datos para el período seleccionado
      </div>
    );
  }

  // Aggregate revenue per service (latest snapshot per service)
  const byService = data.reduce<Record<string, ServiceRevenueMetric>>((acc, item) => {
    if (!acc[item.serviceId] || item.snapshotDate > acc[item.serviceId].snapshotDate) {
      acc[item.serviceId] = item;
    }
    return acc;
  }, {});

  const sorted = Object.values(byService).sort((a, b) => Number(b.revenue) - Number(a.revenue));
  const names = sorted.map((d) => d.serviceName);
  const revenues = sorted.map((d) => Number(d.revenue));

  const palette = ["#3B82F6", "#8B5CF6", "#06B6D4", "#22C55E", "#F59E0B", "#EF4444", "#EC4899"];

  const option = {
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      backgroundColor: "#fff",
      borderColor: "#e2e8f0",
      borderWidth: 1,
      textStyle: { color: "#1e293b", fontSize: 12 },
      formatter: (params) => {
        const idx = params[0].dataIndex;
        const item = sorted[idx];
        return `<div style="font-weight:600;margin-bottom:4px">${item.serviceName}</div>
          <div style="color:#64748b">Ingresos: <b style="color:#1e293b">$${formatCurrency(Number(item.revenue))}</b></div>
          <div style="color:#64748b">Suscripciones: <b style="color:#1e293b">${item.subscriptionCount}</b></div>
          <div style="color:#64748b">Slots: <b style="color:#1e293b">${item.slotCount}</b></div>
          <div style="color:#64748b">Cuentas completas: <b style="color:#1e293b">${item.fullAccountCount}</b></div>
          <div style="color:#64748b">Ranking: <b style="color:#1e293b">#${item.ranking}</b></div>`;
      },
    },
    grid: { left: "3%", right: "8%", bottom: "4%", top: "4%", containLabel: true },
    xAxis: {
      type: "value",
      axisLine: { show: false },
      splitLine: { lineStyle: { color: "#f1f5f9", type: "dashed" } },
      axisLabel: {
        color: "#94a3b8",
        fontSize: 10,
        formatter: (v) => `$${formatCurrency(v)}`,
      },
    },
    yAxis: {
      type: "category",
      data: names,
      axisLine: { lineStyle: { color: "#e2e8f0" } },
      axisLabel: { color: "#64748b", fontSize: 12, fontWeight: "500" },
      inverse: true,
    },
    series: [
      {
        type: "bar",
        data: revenues.map((v, i) => ({
          value: v,
          itemStyle: {
            color: {
              type: "linear",
              x: 0, y: 0, x2: 1, y2: 0,
              colorStops: [
                { offset: 0, color: palette[i % palette.length] },
                { offset: 1, color: palette[i % palette.length] + "60" },
              ],
            },
            borderRadius: [0, 4, 4, 0],
          },
        })),
        barMaxWidth: 28,
        label: {
          show: true,
          position: "right",
          formatter: (params) => `$${formatCurrency(params.value as number)}`,
          color: "#64748b",
          fontSize: 11,
          fontWeight: "600",
        },
      },
    ],
  };

  return <ReactECharts option={option} style={{ height: Math.max(260, sorted.length * 50) }} opts={{ renderer: "svg" }} />;
}
